import { Button, Form, InputGroup } from "react-bootstrap";
import React, { Children, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { collection, getDocs, query, where, orderBy, getDoc, doc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../../context/AuthProvider";
import CreateAccount from "../CreateAccount";
import SignIn from "../SignIn";
import { db } from "../../firebase";
import { useMultiStepForm } from "../hooks/useMultiStepForm";
import { createCheckout, onCreateUser, registerWithMembership, writeUserDetails } from "../../func/writeFunctions";
import { useUserContext } from "../../context/UserProvider";
import TeamForm from "./TeamForm";
import { ConfirmationPage } from "./ConfirmationPage";
import { MedalIcon, TruckIcon } from "../util/Icons";
import { GiftCodesForm } from "./GiftCodesForm";
import { SendAnalyticsEvent, SendBeginCheckout, SendCheckoutProgress, SendPageView } from "../../func/analyticsFunctions";
import { isInviteVisit } from "./RaceReady/inviteLink";
import AddressProfileFields from "../util/AddressProfileFields";
import {
  MEMBER_REGISTRATION_STATUS,
  formatMemberFreeRegistrationDate,
  getMemberRegistrationAvailability,
} from "./memberRegistrationAvailability";
import { normalizeCheckoutProduct } from "./checkoutProductUtils";
import {
  getBillingPeriod,
  getCreditWindow,
  getMemberClaimsInPeriod,
  getPremiumCreditStatus,
  isPremiumMemberChallenge,
} from "./premiumChallengeCredit";
import { applyPromoDiscount, describePromoDiscount, useAppliedPromo } from "./promoDisplay";

function isPreviewOrigin(origin = "") {
  return origin === "https://mcc-staging-4525c.web.app" || origin === "http://localhost:3000";
}

function getMemberPaidRegistrationPromoCode(raceData) {
  const memberPaidRegistrationPromoCode = raceData?.metadata?.memberPaidRegistrationPromoCode;
  if (typeof memberPaidRegistrationPromoCode !== "string") return null;

  const promoCode = memberPaidRegistrationPromoCode.trim();
  return promoCode.length > 0 ? promoCode : null;
}

function getFreeMembersChallenge(optionData) {
  if (!optionData || typeof optionData !== "object") return null;
  if (typeof optionData.name !== "string" || typeof optionData.url !== "string") return null;

  const name = optionData.name.trim();
  const url = optionData.url.trim();
  if (!name || !url) return null;

  return { name, url };
}

function isExpiredPromotionCodeError(error) {
  const message = typeof error === "string" ? error : error?.message || "";
  return /promotion code has expired/i.test(message);
}

const RacePage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const referral_link = params.get("referral_link");
  const promo_code = params.get("promo_code");
  const promo_product = params.get("promo_product");
  const previewMode = params.get("preview") === "1" && isPreviewOrigin(window.location.origin);
  const previewConfirmation = previewMode && location.pathname.endsWith("/confirmation");
  const navigate = useNavigate();
  const { user } = useAuth();
  let { id } = useParams();
  const [priceList, setPriceList] = useState([]);
  const [race, setRace] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [stepsVisited, setStepsVisited] = useState(0);
  const [hasValidMemberPaidRegistrationPromoCode, setHasValidMemberPaidRegistrationPromoCode] = useState(false);
  const [freeMembersChallenge, setFreeMembersChallenge] = useState(null);
  const [membershipPortalLoading, setMembershipPortalLoading] = useState(false);
  const [expiredPromoRecovery, setExpiredPromoRecovery] = useState(null);
  const [memberRegistrationNowMs, setMemberRegistrationNowMs] = useState(Date.now());
  const { userRaces, membershipAccess } = useUserContext()

  // A friend opened an invite link someone shared after registering.
  useEffect(() => {
    if (previewMode || location.pathname.endsWith("/confirmation") || !isInviteVisit(location.search)) return;
    SendAnalyticsEvent("race_invite_referred_visit", { race: id, referral_link: referral_link || "" });
    // Once per challenge page load, not on every checkout step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const memberRegistrationAvailability = useMemo(() => {
    return getMemberRegistrationAvailability(race?.data, memberRegistrationNowMs);
  }, [race, memberRegistrationNowMs]);
  const raceAllowsMemberFreeRegistration = useMemo(() => {
    return memberRegistrationAvailability.status === MEMBER_REGISTRATION_STATUS.FREE;
  }, [memberRegistrationAvailability.status]);
  // One free premium challenge per membership payment (premiumChallengeCredit.js).
  const memberSubscription = membershipAccess?.subscription || null;
  const memberPeriodStartMs = getBillingPeriod(memberSubscription)?.startMs ?? null;
  const premiumCreditKey =
    isPremiumMemberChallenge(race?.data) &&
    user?.uid &&
    membershipAccess?.canUseFreeRace === true &&
    memberRegistrationAvailability.status !== MEMBER_REGISTRATION_STATUS.PAID &&
    memberPeriodStartMs !== null
      ? `${user.uid}:${race.id}:${memberSubscription?.id || ""}:${memberPeriodStartMs}`
      : null;
  const [premiumCredit, setPremiumCredit] = useState({ key: null, status: null });
  const isPremiumCreditPending = premiumCreditKey !== null && premiumCredit.key !== premiumCreditKey;
  const premiumCreditStatus = premiumCreditKey !== null && premiumCredit.key === premiumCreditKey
    ? premiumCredit.status
    : null;
  const isPremiumCreditUsed = premiumCreditStatus?.used === true;
  useEffect(() => {
    if (!premiumCreditKey) return undefined;
    let cancelled = false;
    const subscription = memberSubscription;
    const raceId = race.id;

    async function loadPremiumCredit() {
      try {
        const paymentsSnap = await getDocs(query(
          collection(db, "stripe_customers", user.uid, "payments"),
          where("registerAsMember", "==", true)
        ));
        const payments = paymentsSnap.docs.map((paymentDoc) => ({ ...paymentDoc.data(), id: paymentDoc.id }));
        const claims = getMemberClaimsInPeriod(payments, getCreditWindow(subscription), { excludeRaceId: raceId });
        const productsById = {};
        await Promise.all(
          [...new Set(claims.map((payment) => payment.metadata.raceId))].map(async (claimRaceId) => {
            const productSnap = await getDoc(doc(db, "stripe_products", claimRaceId));
            if (productSnap.exists()) productsById[claimRaceId] = productSnap.data();
          })
        );
        const status = getPremiumCreditStatus({ subscription, payments: claims, productsById });
        if (!cancelled) setPremiumCredit({ key: premiumCreditKey, status });
      } catch (error) {
        // The server checks again when the member claims, so fall back to the normal flow.
        console.error("Unable to check premium challenge credit", error);
        if (!cancelled) setPremiumCredit({ key: premiumCreditKey, status: { used: false } });
      }
    }

    loadPremiumCredit();
    return () => {
      cancelled = true;
    };
    // premiumCreditKey covers the user, race, subscription and billing period.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiumCreditKey]);
  const canUseMemberFreeRegistration = useMemo(() => {
    return (
      membershipAccess?.canUseFreeRace === true &&
      raceAllowsMemberFreeRegistration &&
      !isPremiumCreditPending &&
      !isPremiumCreditUsed
    );
  }, [membershipAccess?.canUseFreeRace, raceAllowsMemberFreeRegistration, isPremiumCreditPending, isPremiumCreditUsed]);
  const canShowPremiumCreditUsed = isPremiumCreditUsed;
  const premiumCreditUnlocksAtMs = useMemo(() => {
    if (!isPremiumCreditUsed) return null;
    const times = [premiumCreditStatus?.nextCreditAtMs, memberRegistrationAvailability.startsAtMs]
      .filter((time) => Number.isFinite(time));
    return times.length > 0 ? Math.max(...times) : null;
  }, [isPremiumCreditUsed, premiumCreditStatus, memberRegistrationAvailability.startsAtMs]);
  const memberPaidRegistrationPromoCode = useMemo(() => getMemberPaidRegistrationPromoCode(race?.data), [race]);
  const canOfferMemberPaidRegistrationDiscount = useMemo(() => {
    return membershipAccess?.canUseMemberRaceDiscount === true && !canUseMemberFreeRegistration && hasValidMemberPaidRegistrationPromoCode;
  }, [membershipAccess?.canUseMemberRaceDiscount, canUseMemberFreeRegistration, hasValidMemberPaidRegistrationPromoCode]);
  const canShowScheduledMemberFreeRegistration = useMemo(() => {
    return (
      membershipAccess?.canUseFreeRace === true &&
      memberRegistrationAvailability.status === MEMBER_REGISTRATION_STATUS.SCHEDULED_FREE &&
      !isPremiumCreditUsed
    );
  }, [membershipAccess?.canUseFreeRace, memberRegistrationAvailability.status, isPremiumCreditUsed]);
  const memberRegistrationNoticeType = useMemo(() => {
    const hasPausedMemberRacePerk = raceAllowsMemberFreeRegistration || hasValidMemberPaidRegistrationPromoCode;
    if (!hasPausedMemberRacePerk || canUseMemberFreeRegistration || canOfferMemberPaidRegistrationDiscount) {
      return null;
    }
    if (membershipAccess?.state === "past_due_grace" || membershipAccess?.state === "past_due_expired") {
      return "past_due";
    }
    if (membershipAccess?.state === "trialing") {
      return "trialing";
    }
    return null;
  }, [
    canOfferMemberPaidRegistrationDiscount,
    canUseMemberFreeRegistration,
    hasValidMemberPaidRegistrationPromoCode,
    membershipAccess?.state,
    raceAllowsMemberFreeRegistration,
  ]);
  const isMemberPaidRegistrationDiscountApplied = useMemo(() => {
    return canOfferMemberPaidRegistrationDiscount && promo_code === memberPaidRegistrationPromoCode;
  }, [canOfferMemberPaidRegistrationDiscount, promo_code, memberPaidRegistrationPromoCode]);
  const isReplacingExistingPromoCode = useMemo(() => {
    return canOfferMemberPaidRegistrationDiscount && !!promo_code && promo_code !== memberPaidRegistrationPromoCode;
  }, [canOfferMemberPaidRegistrationDiscount, promo_code, memberPaidRegistrationPromoCode]);
  const memberPaidRegistrationDiscountUrl = useMemo(() => {
    if (!memberPaidRegistrationPromoCode) return null;

    const params = new URLSearchParams(location.search);
    params.set("promo_code", memberPaidRegistrationPromoCode);
    const nextSearch = params.toString();
    return nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname;
  }, [location.pathname, location.search, memberPaidRegistrationPromoCode]);
  const isAlreadyRegistered = useMemo(() => {
    if (!userRaces || !priceList || !priceList?.length > 0) return null
    if (typeof userRaces !== typeof new Map()) return null
    for (let price of priceList) {
      if (userRaces.has(price?.id)) {
        return true
      }
    }
    return false
  }, [userRaces, priceList])
  const isRegistering = useRef(false)

  async function openMembershipPortal() {
    if (membershipPortalLoading) return;
    setMembershipPortalLoading(true);
    try {
      const functionRef = httpsCallable(
        getFunctions(),
        "ext-firestore-stripe-payments-createPortalLink",
      );
      const { data } = await functionRef({
        returnUrl: window.location.href,
      });
      window.open(data.url, "_self");
    } catch (error) {
      console.error("Unable to open billing portal", error);
      setCheckoutError("We couldn't open your billing page. Please try again.");
      setMembershipPortalLoading(false);
    }
  }

  const formsArray = [
    <div>
      {memberRegistrationNoticeType && (
        <MemberRegistrationPausedNotice
          type={memberRegistrationNoticeType}
          onUpdatePayment={openMembershipPortal}
          isLoading={membershipPortalLoading}
        />
      )}
      <div id="regular-registration-form">
        <RaceForm priceList={priceList} race={race} canUseMemberFreeRegistration={canUseMemberFreeRegistration} />
      </div>
    </div>,
    <GiftCodesForm priceList={priceList} race={race} />,
    <DonationForm />,
    <ProductsForm />,
  ];
  const memberForm = [
    <div className="">
      <div className="flex text-white items-center justify-center rounded-xl shadow-md gap-2 px-2 py-1"
        style={{ backgroundImage: "linear-gradient(to right, #006cbe, #f3b155)" }}>
        <MedalIcon size={36} color="white" />
        <div>
          <p className="m-0 text-lg font-semibold">Member Perk: Run this challenge for FREE.</p>
          <p className="m-0 text-sm font-medium">Use your monthly race credit to lock in your spot immediately.</p>
        </div>
      </div>
      <RaceForm priceList={priceList} race={race} canUseMemberFreeRegistration={canUseMemberFreeRegistration} />
    </div>,
  ];
  const memberDiscountForm = [
    <div className="">
      <MemberPaidRegistrationBanner
        applyDiscountUrl={memberPaidRegistrationDiscountUrl}
        freeMembersChallenge={freeMembersChallenge}
        isDiscountApplied={isMemberPaidRegistrationDiscountApplied}
        isReplacingExistingPromoCode={isReplacingExistingPromoCode}
      />
      <RaceForm priceList={priceList} race={race} canUseMemberFreeRegistration={canUseMemberFreeRegistration} />
    </div>,
    <GiftCodesForm priceList={priceList} race={race} />,
    <DonationForm />,
    <ProductsForm />,
  ];
  const premiumCreditUsedForm = [
    <div className="">
      <MemberPremiumCreditUsedBanner
        unlocksAtMs={premiumCreditUnlocksAtMs}
        usedOnRaceName={premiumCreditStatus?.usedOn?.raceName || ""}
        raceEndDate={race?.data?.metadata?.raceEndDate}
        isCancelling={membershipAccess?.state === "canceling_active"}
        membershipEndsAtMs={membershipAccess?.accessEndsAt}
      />
      <RaceForm priceList={priceList} race={race} canUseMemberFreeRegistration={canUseMemberFreeRegistration} />
    </div>,
    <GiftCodesForm priceList={priceList} race={race} />,
    <DonationForm />,
    <ProductsForm />,
  ];
  const scheduledMemberFreeForm = [
    <div className="">
      <MemberScheduledFreeRegistrationBanner startsAtMs={memberRegistrationAvailability.startsAtMs} />
      <RaceForm priceList={priceList} race={race} canUseMemberFreeRegistration={canUseMemberFreeRegistration} />
    </div>,
    <GiftCodesForm priceList={priceList} race={race} />,
    <DonationForm />,
    <ProductsForm />,
  ];
  // if (!promo_code) {
  //   formsArray.splice(1, 0, <GiftCodesForm priceList={priceList} race={race} />);
  // }
  const formDisabled = isAlreadyRegistered
  const { CurrentForm, formDetails, formLoading, currentStepIndex, goToFormStep, setFormDetails } = useMultiStepForm(
    canUseMemberFreeRegistration
      ? memberForm
      : canShowPremiumCreditUsed
        ? premiumCreditUsedForm
        : canShowScheduledMemberFreeRegistration
        ? scheduledMemberFreeForm
        : canOfferMemberPaidRegistrationDiscount
          ? memberDiscountForm
          : formsArray,
    handleSubmit,
    formDisabled,
    ({ currentStepIndex }) => {
      const checkoutBanner = race?.data?.metadata?.checkoutBanner;
      if (currentStepIndex !== 0 || !checkoutBanner) return null;

      return (
        <picture>
          <img
            className="w-full max-w-lg sm:max-w-md px-3 pt-3 mr-auto text-sm"
            src={checkoutBanner}
            alt="Promotional banner featuring race details."
            title="Race to the Finish"
          ></img>
        </picture>
      );
    }
  );

  useEffect(() => {
    if (memberRegistrationAvailability.status !== MEMBER_REGISTRATION_STATUS.SCHEDULED_FREE) return undefined;

    const remainingMs = memberRegistrationAvailability.startsAtMs - Date.now();
    const maximumTimeoutMs = 2_147_000_000;
    const timeoutId = window.setTimeout(
      () => setMemberRegistrationNowMs(Date.now()),
      Math.max(0, Math.min(remainingMs + 1000, maximumTimeoutMs))
    );

    return () => window.clearTimeout(timeoutId);
  }, [memberRegistrationAvailability]);

  const priceMap = new Map();
  priceList.forEach((price) => {
    priceMap.set(price.id, price.data);
  });

  useEffect(() => {
    if (previewConfirmation) return;
    const q = query(collection(db, "stripe_products"), where("stripe_metadata_url", "==", id));
    async function fetchRace() {
      const querySnapshot = await getDocs(q);
      if (querySnapshot.docs[0]) {
        if (querySnapshot.docs[0].data().metadata.type !== "race" || !querySnapshot.docs[0].data().active) {
          navigate("/404", { replace: true });
        }
        let priceMap = [];
        let snapRace = querySnapshot.docs[0];
        const raceData = snapRace.data();
        const prices = await getDocs(query(collection(db, "stripe_products", snapRace.id, "prices"), orderBy("unit_amount", "asc")));
        prices.forEach((price) => {
          if (price.data().active) {
            const docID = price.data()?.metadata?.doc_id ? price.data().metadata.doc_id : price.id;
            // console.log(docID)
            priceMap.push({ id: docID, data: price.data() });
          }
        });
        const configuredMemberPromoCode = getMemberPaidRegistrationPromoCode(raceData);
        if (configuredMemberPromoCode) {
          try {
            const validPromoCodesDoc = await getDoc(doc(db, "options", "validPromoCodes"));
            const validPromoCodes = validPromoCodesDoc.data()?.codes || {};
            setHasValidMemberPaidRegistrationPromoCode(
              Object.prototype.hasOwnProperty.call(validPromoCodes, configuredMemberPromoCode)
            );
          } catch (error) {
            console.error("Unable to load member registration promo settings", error);
            setHasValidMemberPaidRegistrationPromoCode(false);
          }
        } else {
          setHasValidMemberPaidRegistrationPromoCode(false);
        }
        setFreeMembersChallenge(null);
        setMemberRegistrationNowMs(Date.now());
        setRace({ id: snapRace.id, data: raceData });
        setPriceList(priceMap);
        return;
      }
      navigate("/404", { replace: true });
    }
    fetchRace();
  }, [id, navigate, previewConfirmation]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [CurrentForm]);

  useEffect(() => {
    // Set Page Title
    let lo = location.pathname;
    let checkoutNames = ["Leg Selection", "Gift Codes", "Donation", "Products"];
    if (!race) return;
    switch (true) {
      case lo.endsWith("/signin"):
        document.title = "Sign In - " + race?.data?.name;
        SendPageView("Sign In - " + race?.data?.name);
        break;
      case lo.endsWith("/createaccount"):
        document.title = "Create Account - " + race?.data?.name;
        SendPageView("Create Account - " + race?.data?.name);
        break;
      case lo.endsWith("/confirmation"):
        // Handled by confirm page
        // document.title = "Confirmation - " + race?.data?.name;
        break;
      default:
        let currentStep = currentStepIndex + 1;
        document.title = `Checkout - Step ${currentStepIndex + 1} - ${checkoutNames[currentStepIndex]} - ${race?.data?.name}`;
        SendPageView(`Checkout - Step ${currentStepIndex + 1} - ${checkoutNames[currentStepIndex]} - ${race?.data?.name}`);
        if (user === null) return;
        if (currentStep > stepsVisited) {
          setStepsVisited(currentStep);
          if (currentStepIndex === 0) {
            SendBeginCheckout(user.uid, race?.data?.name);
            // console.log("Begin checkout");
          } else {
            SendCheckoutProgress(user.uid, race?.data?.name, currentStep, checkoutNames[currentStepIndex]);
            // console.log("Checkout progress", currentStep);
          }
        }
        break;
    }
  }, [location.pathname, race, currentStepIndex]);

  async function getExpiredPromotionFallback() {
    try {
      const validPromoCodesDoc = await getDoc(doc(db, "options", "validPromoCodes"));
      const validPromoCodesData = validPromoCodesDoc.data() || {};
      const validPromoCodes = validPromoCodesData.codes || {};
      const fallback = validPromoCodesData.promoRecovery?.expiredPromotionFallback;
      const fallbackPromoCode = typeof fallback?.promoCode === "string" ? fallback.promoCode.trim() : "";

      if (fallback?.active !== true || !fallbackPromoCode) return null;
      if (!Object.prototype.hasOwnProperty.call(validPromoCodes, fallbackPromoCode)) return null;

      return {
        promoCode: fallbackPromoCode,
        buttonLabel: fallback.buttonLabel || "Continue with $5 off",
        message:
          fallback.message ||
          "This offer has ended, but we can still give you $5 off your registration.",
      };
    } catch (error) {
      console.error("Unable to load expired promotion recovery settings", error);
      return null;
    }
  }

  function continueWithFallbackPromo() {
    if (!expiredPromoRecovery?.promoCode) return;

    setFormDetails((prev) => {
      const nextFormDetails = { ...prev };
      if (promo_product) {
        delete nextFormDetails[`product_${promo_product}`];
      }
      return nextFormDetails;
    });

    const nextParams = new URLSearchParams(location.search);
    nextParams.set("promo_code", expiredPromoRecovery.promoCode);
    nextParams.delete("promo_product");
    const nextSearch = nextParams.toString();

    setCheckoutError(null);
    setExpiredPromoRecovery(null);
    goToFormStep(1);
    navigate(nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname, { replace: true });
  }

  async function handleSubmit(formDetails) {
    let extraUserDetails = {
      raceId: race.id,
    };
    priceList.forEach((price) => {
      if (price.id === formDetails.distanceId) {
        extraUserDetails.distanceString = price.data.description;
        extraUserDetails.distancePrice = price.data.unit_amount;
      }
    });
    if (canUseMemberFreeRegistration === true) {
      // console.log("Register as member");
      if (isRegistering.current) return
      isRegistering.current = true
      try {
        await onCreateUser(user);
        await writeUserDetails(user, { ...formDetails, ...extraUserDetails });
        const registrationResult = await registerWithMembership(user, { ...formDetails, ...extraUserDetails }, race);
        if (registrationResult === true) {
          // await new Promise(resolve => {
          //   setTimeout(() => resolve(), 200)
          // })
          navigate(`/register/${race.data.metadata.url}/confirmation?race=${formDetails.distanceId.replace(/^price_/, "")}`,);
          return true;
        } else {
          setCheckoutError(typeof registrationResult === "string" ? registrationResult : "Unable to register with your membership.");
          return true;
        }
      } finally {
        isRegistering.current = false
      }
    }
    // console.log(extraUserDetails);
    const q = query(collection(db, "stripe_products"), where("name", "==", "Service Fee"));
    try {
      const querySnapshot = await getDocs(q);
      if (querySnapshot.docs[0]) {
        const serviceFeeId = querySnapshot.docs[0].id;
        const serviceFeePrices = await getDocs(
          query(collection(db, "stripe_products", serviceFeeId, "prices"), orderBy("unit_amount", "desc"))
        );
        if (serviceFeePrices.docs[0]) {
          extraUserDetails.serviceFeePriceId = serviceFeePrices.docs[0].id;
        }
      }

      const finalFormDetails = { ...formDetails, ...extraUserDetails };
      if (promo_code) {
        finalFormDetails.promoCode = promo_code;
      }

      await onCreateUser(user);
      await writeUserDetails(user, finalFormDetails);
      // await writeUserToRace(user, finalFormDetails);
      // console.log("Submitting...", finalFormDetails, race, id);

      setExpiredPromoRecovery(null);
      const createCheckoutError = await createCheckout(user, finalFormDetails, race, id, priceMap, referral_link);
      console.log(createCheckoutError);
      if (createCheckoutError) {
        if (isExpiredPromotionCodeError(createCheckoutError)) {
          const fallback = await getExpiredPromotionFallback();
          setExpiredPromoRecovery(fallback);
        }
        setCheckoutError(createCheckoutError);
        return true;
      }
      // navigate("success", { replace: true });
    } catch (err) {
      console.log(err);
      return true;
    }
  }

  if (priceList.length <= 0 && !previewConfirmation) {
    return;
  }

  // console.log(race.data)
  const selectedRace = race?.data;
  const createAccountURL = "createaccount" + window.location.search;
  // if (referral_link) {
  //   createAccountURL += `?referral_link=${referral_link}`;
  // }
  return (
    // <div className="relative mx-auto max-w-3xl mt-6 w-full">
    <div className="h-full">
      {/* <div className="mt-3 max-w-2xl md:w-full">
        {race && !(selectedRace.metadata.checkoutBanner && window.location.pathname.includes("createaccount")) && (
          <div className="grid grid-cols-[62px,1fr] gap-3 items-center mb-4 px-2">
            <img src={selectedRace?.images[0]} alt={`${selectedRace?.name} Logo`} />
            <div>
              <h2 className="my-0">{selectedRace?.name}</h2>
              <p className="my-0 text-sm">
                {selectedRace.metadata.userStartDate} - {selectedRace.metadata.userEndDate}
              </p>
            </div>
          </div>
        )}
      </div> */}
      <div className="h-full relative">
        <Routes>
          <Route
            path={`/`}
            element={
              user ? (
                checkoutError ? (
                  <RacePageMargin>
                    {expiredPromoRecovery && isExpiredPromotionCodeError(checkoutError) ? (
                      <ExpiredPromotionRecovery
                        promoCode={promo_code}
                        recovery={expiredPromoRecovery}
                        onContinue={continueWithFallbackPromo}
                      />
                    ) : (
                      <div className="text-center">
                        <p>{checkoutError}</p>
                        <Button
                          className="!text-sm"
                          onClick={() => setCheckoutError(null)}
                        >
                          Go back
                        </Button>
                      </div>
                    )}
                  </RacePageMargin>
                ) : (
                  <RacePageMargin>
                    {isAlreadyRegistered && (
                      <p className="text-center">
                        You're already registered to this race,{" "}
                        <Link to={"/"}>Go back</Link>
                      </p>
                    )}
                    <CurrentForm />
                  </RacePageMargin>
                )
              ) : (
                <Navigate
                  to={createAccountURL}
                  state={{ from: location.state?.from || window.location.pathname + window.location.search }}
                />
              )
            }
          ></Route>
          <Route
            path={`/confirmation`}
            element={
              user || previewConfirmation ? (
                <RacePageMargin>
                  <ConfirmationPage raceName={id} />
                </RacePageMargin>
              ) : (
                <Navigate to={createAccountURL}
                  state={{ from: location.state?.from || window.location.pathname + window.location.search }}
                />
              )
            }
          ></Route>
          <Route
            path={`/signin`}
            element={
              <>
                <SignIn
                  checkoutBanner={selectedRace?.metadata?.checkoutBanner}
                  background={selectedRace?.metadata?.checkoutBackground}
                  backgroundMobile={selectedRace?.metadata?.checkoutBackgroundMobile}
                />
              </>
            }
          ></Route>
          <Route
            path={`/createaccount`}
            element={
              <>
                <CreateAccount
                  checkoutBanner={selectedRace?.metadata?.checkoutBanner}
                  background={selectedRace?.metadata?.checkoutBackground}
                  backgroundMobile={selectedRace?.metadata?.checkoutBackgroundMobile}
                />
              </>
            }
          ></Route>
          <Route path="*" element={<Navigate to="../" state={location.state?.from ? { from: location.state?.from } : {}} />} />
        </Routes>
      </div>
    </div>
  );
};

export default RacePage;

function ExpiredPromotionRecovery({ promoCode, recovery, onContinue }) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-3 text-center">
      <h2 className="mb-3 text-3xl font-semibold">This offer has ended</h2>
      <p className="mb-2 text-base text-slate-700">
        {promoCode ? `The ${promoCode} promo is no longer available.` : "This promo is no longer available."}
      </p>
      <p className="mb-4 text-base text-slate-700">{recovery.message}</p>
      <Button className="!text-sm" onClick={onContinue}>
        {recovery.buttonLabel}
      </Button>
    </div>
  );
}

function RacePageMargin({ children }) {
  return <div className="relative mx-auto max-w-3xl mt-6 w-full mb-3 h-5/6">{children}</div>;
}

function MemberPaidRegistrationBanner({
  applyDiscountUrl,
  freeMembersChallenge,
  isDiscountApplied = false,
  isReplacingExistingPromoCode = false,
}) {
  if (!applyDiscountUrl && !isDiscountApplied) return null;

  return (
    <div className="rounded-xl border border-[#d6e3b1] bg-[#f8fbe9] px-3 py-3 shadow-md">
      <div>
        <div>
          <p className="m-0 text-lg font-semibold text-[#20350a]">Member Perk: Save $20 on this race.</p>
          <p className="m-0 text-sm font-medium text-[#4f6520]">
            This challenge is not part of your free member credit, but your membership still gives you $20 off at checkout.
          </p>
          {isReplacingExistingPromoCode && (
            <p className="m-0 mt-1 text-xs font-semibold text-[#9a5a00]">
              Applying this offer will replace the promo code already on this page.
            </p>
          )}
        </div>
      </div>
      <div className="mt-3">
        {isDiscountApplied ? (
          <span className="inline-flex rounded-full bg-[#e8f6ec] px-3 py-1 text-sm font-semibold text-green-700">
            $20 member discount applied
          </span>
        ) : (
          <Button as={Link} to={applyDiscountUrl} variant="success" className="!text-sm !font-semibold">
            Apply My $20 Discount
          </Button>
        )}
        {freeMembersChallenge && (
          <p className="m-0 mt-3 text-sm font-medium text-[#4f6520]">
            This month&apos;s free challenge included in your membership is:{" "}
            <a href={freeMembersChallenge.url} className="font-semibold underline">
              {freeMembersChallenge.name}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

function MemberScheduledFreeRegistrationBanner({ startsAtMs }) {
  const availableDate = formatMemberFreeRegistrationDate(startsAtMs);
  if (!availableDate) return null;

  return (
    <div
      className="flex items-center justify-center gap-2 rounded-xl px-2 py-1 text-white shadow-md"
      style={{ backgroundImage: "linear-gradient(to right, #006cbe, #f3b155)" }}
    >
      <MedalIcon size={36} color="white" />
      <div>
        <p className="m-0 text-lg font-semibold">
          Member Perk: This challenge will be FREE starting {availableDate}.
        </p>
        <p className="m-0 text-sm font-medium">
          Wait until then to use your free race credit, or continue with paid registration today.
        </p>
      </div>
    </div>
  );
}

export function MemberPremiumCreditUsedBanner({
  unlocksAtMs,
  usedOnRaceName = "",
  raceEndDate,
  isCancelling = false,
  membershipEndsAtMs = null,
}) {
  const unlockDate = formatMemberFreeRegistrationDate(unlocksAtMs);
  const raceEndMs = typeof raceEndDate === "string" ? Date.parse(raceEndDate) : NaN;
  const unlocksAfterRaceEnds = Number.isFinite(unlocksAtMs) && Number.isFinite(raceEndMs) && unlocksAtMs > raceEndMs;
  const usedOn = usedOnRaceName ? ` on ${usedOnRaceName}` : "";

  let title;
  let detail;
  if (isCancelling) {
    // Cancelled but still paid: there is no next payment, so no next credit.
    const endsOn = formatMemberFreeRegistrationDate(membershipEndsAtMs);
    title = "Member Perk: You've already used your FREE premium challenge for this quarter.";
    detail = `You already used this quarter's premium challenge${usedOn}` +
      (endsOn ? `, and your membership ends ${endsOn}.` : ", and your membership is set to end.") +
      " Keep your membership to get your next free premium challenge, or continue with paid registration today.";
  } else if (!unlockDate) {
    title = "Member Perk: You've already used your FREE premium challenge for this quarter.";
    detail = `You used it${usedOn}. Your next one comes with your next membership payment, or continue with paid registration today.`;
  } else if (unlocksAfterRaceEnds) {
    title = `Member Perk: Your next FREE premium challenge unlocks on ${unlockDate}.`;
    detail = `You already used this quarter's premium challenge${usedOn}. That's after this challenge ends, so continue with paid registration to join today.`;
  } else {
    title = `Member Perk: This challenge will be FREE for you starting ${unlockDate}.`;
    detail = `You already used this quarter's premium challenge${usedOn}. Wait until then to use your next free premium credit, or continue with paid registration today.`;
  }

  return (
    <div
      className="flex items-center justify-center gap-2 rounded-xl px-2 py-1 text-white shadow-md"
      style={{ backgroundImage: "linear-gradient(to right, #006cbe, #f3b155)" }}
    >
      <MedalIcon size={36} color="white" />
      <div>
        <p className="m-0 text-lg font-semibold">{title}</p>
        <p className="m-0 text-sm font-medium">{detail}</p>
      </div>
    </div>
  );
}

function MemberRegistrationPausedNotice({ type, onUpdatePayment, isLoading = false }) {
  const isPastDue = type === "past_due";
  const scrollToForm = () => {
    document.getElementById("regular-registration-form")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="mb-3 rounded-xl border border-[#fed7aa] bg-[#fff7ed] px-3 py-3 shadow-md">
      <p className="m-0 text-lg font-semibold text-[#9a3412]">
        {isPastDue ? "Action Required: Membership Paused" : "Free member challenge credit pending"}
      </p>
      <p className="m-0 mt-1 text-sm font-medium text-[#9a3412]">
        {isPastDue
          ? "We couldn’t process your latest payment. Update your payment method now to reactivate your free race perks and full member access. Or you can continue with a paid registration for this challenge."
          : "Your member content is active, and your free member challenge credit unlocks after your first membership payment is complete. You can continue with regular paid registration today."}
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        {isPastDue && (
          <Button
            type="button"
            variant="warning"
            className="!text-sm !font-semibold"
            onClick={onUpdatePayment}
            disabled={isLoading}
          >
            {isLoading ? "Opening billing..." : "Update payment method"}
          </Button>
        )}
        <Button
          type="button"
          variant="outline-secondary"
          className="!text-sm !font-semibold"
          onClick={scrollToForm}
        >
          Continue paid registration
        </Button>
      </div>
    </div>
  );
}

export function RaceForm({ priceList, race, canUseMemberFreeRegistration = false }) {
  const { userDetails, userData } = useUserContext();
  const [selectedRace, setSelectedRace] = useState(null);
  const hasSingleDistanceOption = priceList.length === 1;
  // Shows the URL's promo code here too; Stripe still applies the real discount at checkout.
  const promo = useAppliedPromo(new URLSearchParams(window.location.search).get("promo_code"));
  const promoLabel = describePromoDiscount(promo);
  // label carries its own spacing, e.g. "Early Bird - " or "Regular ".
  const currentPrice = (label, price) => {
    const discounted = applyPromoDiscount(price, promo);
    if (!discounted) return `${label}$${price}`;
    return (
      <>
        {label}
        <span className="font-medium text-[#7A7A7A] line-through">${price}</span>{" "}
        <span className="text-green-700">${discounted}</span>
      </>
    );
  };

  const allowTeams = race.data.metadata.teams === "true";

  const earlyBirdEnd = new Date(race.data.metadata.early_end);
  earlyBirdEnd.setDate(earlyBirdEnd.getDate() + 1);
  earlyBirdEnd.setUTCHours(7);

  const timeNow = new Date();
  const diff = new Date(earlyBirdEnd.getTime() - timeNow.getTime());

  const isEarly = diff > 0;

  useEffect(() => {
    if (hasSingleDistanceOption) {
      setSelectedRace(priceList[0]?.id || null);
      return;
    }

    setSelectedRace(null);
  }, [hasSingleDistanceOption, priceList]);

  // console.log(isEarly);

  return (
    <>
      <Form.Group className="mt-3">
        <h3 className="mb-3 text-2xl text-left">Choose your distance</h3>
        {promo && (
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm font-semibold text-green-800">
            <span aria-hidden="true">✓</span>
            <span>
              Code {promo.code} applied{promoLabel ? `: ${promoLabel} your registration` : ". Your discount shows at checkout"}.
            </span>
          </div>
        )}
        <div className="grid gap-2 lg:gap-4 grid-cols-1 lg:grid-cols-3">
          {priceList.map((price) => {
            const isRegistered = userData ? (userData.subscribedRaces ? !!userData.subscribedRaces[price.id] : false) : false;

            return (
              <Form.Check key={price.id} type="radio" className="relative !mb-0 h-full !pl-0">
                <Form.Check.Input
                  className="peer sr-only"
                  type="radio"
                  name="distanceId"
                  id={price.id}
                  onChange={() => setSelectedRace(price.id)}
                  value={price.id}
                  defaultChecked={hasSingleDistanceOption}
                  disabled={isRegistered}
                  required
                />
                <span className="pointer-events-none absolute right-3 top-3 z-20 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#B0B0B0] bg-white transition-colors duration-200 peer-checked:border-[#006BC1] peer-disabled:opacity-60 after:block after:h-2.5 after:w-2.5 after:rounded-full after:bg-transparent after:transition-colors after:duration-200 peer-checked:after:bg-[#006BC1]">
                </span>
                <Form.Label
                  htmlFor={price.id}
                  className="relative mb-0 flex h-full cursor-pointer flex-col rounded-xl border-2 border-[#E0E0E0] bg-white px-4 pb-2.5 pt-2.5 lg:pb-4 lg:pt-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 peer-checked:border-[#006BC1] peer-checked:shadow-[0_0_0_1px_#006BC1,0_2px_8px_rgba(0,0,0,0.08)] peer-focus-visible:ring-4 peer-focus-visible:ring-[#006BC1]/15 peer-disabled:cursor-not-allowed peer-disabled:opacity-75"
                >
                  <div className="pr-8">
	                    <p className="mb-1 text-xs font-semibold leading-snug text-[#5E6470]">
	                      {price.data.metadata.description}
	                    </p>
	                    <p className="mb-2 text-lg font-extrabold leading-tight tracking-normal text-[#111318] lg:text-xl">
	                      {price.data.description}
	                    </p>

                    {isRegistered && (
                      <span className="mb-3 inline-flex rounded-full bg-[#E8F6EC] px-3 py-1 text-xs font-semibold text-green-700">
                        Already Registered!
                      </span>
                    )}

                    <div className="space-y-3">
                      {isEarly ? (
                        <>
                          <div>
                            <p className="mb-0 text-sm font-semibold leading-snug text-[#006BC1]">
                              {currentPrice("Early Bird - ", price.data.metadata.early_price)}
                            </p>
                            <p className="m-0 text-xs leading-snug text-[#888]">
                              ({race.data.metadata.early_start} - {race.data.metadata.early_end})
                            </p>
                          </div>

                          <div>
                            <p className="mb-0 text-sm font-semibold leading-snug text-[#7A7A7A] line-through">
                              Regular ${price.data.metadata.regular_price}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="mb-0 text-sm font-semibold leading-snug text-[#006BC1]">
                              {currentPrice("Regular ", price.data.metadata.regular_price)}
                            </p>
                            <p className="m-0 text-xs leading-snug text-[#888]">
                              ({race.data.metadata.regular_start} - {race.data.metadata.regular_end})
                            </p>
                          </div>

                          <div>
                            <p className="mb-0 text-sm font-semibold leading-snug text-[#7A7A7A] line-through">
                              Early Bird - ${price.data.metadata.early_price}
                            </p>
                            <p className="m-0 text-xs leading-snug text-[#888]">
                              ({race.data.metadata.early_start} - {race.data.metadata.early_end})
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Form.Label>
              </Form.Check>
            );
          })}
        </div>
      </Form.Group>
      <Form.Group className="mb-3">
        <h3 className="mt-3 text-2xl text-left">Participant Details</h3>
        <div className="flex items-center mb-3">
          <TruckIcon size={22} className="inline mr-3" />
          <p className="inline m-0 underline">Your SWAG kit will be shipped to this address. (when applicable)</p>
        </div>
        <div className="mt-2">
          <AddressProfileFields initialDetails={userDetails} />
        </div>
      </Form.Group>
      {allowTeams && <TeamForm selectedRace={selectedRace} canUseMemberFreeRegistration={canUseMemberFreeRegistration} />}
      {!allowTeams && (
        <Form.Group className="mx-auto w-full mb-3 pt-3">
          <Button size="" className="w-full mx-auto block py-2" type="submit">
            {canUseMemberFreeRegistration ? "Claim My Spot" : "Continue"}
          </Button>
        </Form.Group>
      )}
    </>
  );
}

function ProductsForm() {
  const [products, setProducts] = useState([]);
  const params = new URLSearchParams(window.location.search);
  const promo_product = params.get("promo_product");
  // const promo_code = params.get("promo_code");

  useEffect(() => {
    let cancelled = false;

    async function fetchProducts() {
      try {
        const productDocs = await getDocs(query(collection(db, "stripe_products"), where("stripe_metadata_type", "==", "product")));
        const checkoutProducts = await Promise.all(
          productDocs.docs.map(async (product) => {
            if (!product.data().active) return null;

            const priceQuery = await getDocs(
              query(collection(db, "stripe_products", product.id, "prices"), orderBy("unit_amount", "asc"))
            );

            return normalizeCheckoutProduct(
              { id: product.id, data: product.data() },
              priceQuery.docs.map((price) => ({ id: price.id, data: price.data() }))
            );
          })
        );

        if (!cancelled) {
          setProducts(checkoutProducts.filter(Boolean));
        }
      } catch (error) {
        console.error("Unable to load checkout products", error);
        if (!cancelled) setProducts([]);
      }
    }
    fetchProducts();

    return () => {
      cancelled = true;
    };
  }, []);
  if (!products) {
    return;
  }
  return (
    <Form.Group className="mt-3">
      <h3 className="mb-3">Improve Your Experience</h3>
      <Form.Group className="grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4">
        {products.map((product, i) => {
          const isPromoProduct = !!promo_product ? promo_product === product.id : false;
          return (
            <Form.Check key={product.id} type="checkbox" className="relative !mb-0 h-full !pl-0">
              <Form.Check.Input
                className="peer sr-only"
                type="checkbox"
                name={`product_${product.id}`}
                id={product.priceData.id}
                value={product.priceData.id}
                defaultChecked={isPromoProduct}
              />
              <span className="pointer-events-none absolute right-3 top-3 z-20 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#B0B0B0] bg-white transition-colors duration-200 peer-checked:border-[#006BC1] peer-disabled:opacity-60 after:block after:h-2.5 after:w-2.5 after:rounded-full after:bg-transparent after:transition-colors after:duration-200 peer-checked:after:bg-[#006BC1]">
              </span>
              <Form.Label
                htmlFor={product.priceData.id}
                className="relative mb-0 flex h-full cursor-pointer flex-col rounded-xl border-2 border-[#E0E0E0] bg-white px-4 pb-4 pt-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 peer-checked:border-[#006BC1] peer-checked:shadow-[0_0_0_1px_#006BC1,0_2px_8px_rgba(0,0,0,0.08)] peer-focus-visible:ring-4 peer-focus-visible:ring-[#006BC1]/15 peer-checked:[&_.product-card-cta]:border-[#006BC1] peer-checked:[&_.product-card-cta]:bg-[#006BC1] peer-checked:[&_.product-card-cta]:text-white"
              >
                <div className="flex h-full flex-col gap-3">
                  <div className="flex h-40 items-center justify-center rounded-lg p-3">
                    {product.data.images[0] && (
                      <img
                        className="max-h-36 max-w-full object-contain"
                        src={product.data.images[0]}
                        alt={product.data.name || "Optional add-on"}
                        title={product.data.name || "Optional add-on"}
                      />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <p className="mb-2 text-lg font-extrabold leading-tight tracking-normal text-[#111318] lg:text-xl">
                      {product.data.name || "Optional add-on"}
                    </p>
                    {product.data.description && (
                      <p className="whitespace-pre-line text-sm leading-snug text-[#5E6470]">
                        {product.data.description}
                      </p>
                    )}
                    {product.data.metadata.extra_info && (
                      <p className="whitespace-pre-line text-sm leading-snug text-[#5E6470]">
                        {product.data.metadata.extra_info}
                      </p>
                    )}
                    <p className="product-card-cta mb-0 mt-auto w-full max-w-xs self-center rounded-md border border-[#006BC1] px-3 py-2 text-center text-sm font-semibold text-[#006BC1] transition-colors duration-200">
                      {`${product.data.metadata.cta || "Add"} $${product.priceData.data.unit_amount / 100}`}
                    </p>
                  </div>
                </div>
              </Form.Label>
            </Form.Check>
          );
        })}
      </Form.Group>
      <Form.Group className="mx-auto w-full mb-3 pt-3">
        <Button className="w-full mx-auto block py-2" type="submit">
          Continue
        </Button>
      </Form.Group>
    </Form.Group>
  );
}

function DonationForm() {
  const [donationAmount, setDonationAmount] = useState(0);
  const [charitiesData, setCharitiesData] = useState(null);
  const donationIntent = donationAmount ? (parseInt(donationAmount) > 0 ? true : false) : false;

  useEffect(() => {
    if (!charitiesData) fetchCharities();
    async function fetchCharities() {
      try {
        const charitiesDoc = await getDoc(doc(db, "options", "checkoutCharities"));
        setCharitiesData(charitiesDoc.data());
      } catch (err) {
        console.log(err.message);
      }
    }
  }, []);
  return (
    <Form.Group className="mt-3 flex flex-col gap-3">
      <h3 className="mb-3">
        Make a donation <span>(optional)</span>
      </h3>
      <h5>Select Donation Amount</h5>

      <InputGroup>
        <InputGroup.Text>$</InputGroup.Text>
        <Form.Control
          className=""
          type="number"
          placeholder="0"
          name="donation"
          id="donation"
          min={0}
          pattern="\d+"
          value={donationAmount}
          onChange={(e) => setDonationAmount(e.target.value)}
          onFocus={(e) => {
            if (e.target.value === "0") {
              e.target.value = "";
            }
          }}
          onBlur={(e) => {
            if (e.target.value === "") {
              e.target.value = "0";
            }
          }}
        />
      </InputGroup>

      <h5 className="mt-3">Select Organization to Donate</h5>
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-3 lg:gap-4">
        {charitiesData &&
          Object.keys(charitiesData)
            .sort((a, b) => {
              const aOrder = !!charitiesData[a].order ? charitiesData[a].order : 99;
              const bOrder = !!charitiesData[b].order ? charitiesData[b].order : 99;
              return aOrder - bOrder;
            })
            .map((id) => {
              const active = charitiesData[id].active;
              const name = charitiesData[id].name;
              const description = charitiesData[id].description;
              const image_url = charitiesData[id].image_url;
              const image_alt = charitiesData[id].image_alt;
              const inputId = `foundation_${id.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

              if (!active) return;
              return (
                <Form.Check key={id} type="radio" className="relative !mb-0 h-full !pl-0">
                  <Form.Check.Input
                    className="peer sr-only"
                    type="radio"
                    name="foundation"
                    id={inputId}
                    value={name}
                    required={donationIntent}
                  />
                  <span className="pointer-events-none absolute right-3 top-3 z-20 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#B0B0B0] bg-white transition-colors duration-200 peer-checked:border-[#006BC1] peer-disabled:opacity-60 after:block after:h-2.5 after:w-2.5 after:rounded-full after:bg-transparent after:transition-colors after:duration-200 peer-checked:after:bg-[#006BC1]">
                  </span>
                  <Form.Label
                    htmlFor={inputId}
                    className="relative mb-0 flex h-full cursor-pointer flex-col rounded-xl border-2 border-[#E0E0E0] bg-white px-4 pb-4 pt-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 peer-checked:border-[#006BC1] peer-checked:shadow-[0_0_0_1px_#006BC1,0_2px_8px_rgba(0,0,0,0.08)] peer-focus-visible:ring-4 peer-focus-visible:ring-[#006BC1]/15"
                  >
                    <div className="flex h-full flex-col gap-3 pr-8 lg:pr-0">
                      <div className="flex h-24 items-center justify-center rounded-lg p-3">
                        <img
                          className="max-h-20 max-w-full object-contain"
                          src={image_url}
                          alt={image_alt}
                          title={image_alt}
                        />
                      </div>
                      <div>
                        <p className="mb-2 text-lg font-extrabold leading-tight tracking-normal text-[#111318] lg:text-xl">
                          {name}
                        </p>
                        <p className="m-0 text-sm leading-snug text-[#5E6470]">
                          {description}
                        </p>
                      </div>
                    </div>
                  </Form.Label>
                </Form.Check>
              );
            })}
      </div>

      <p className="my-0 text-sm mx-auto text-slate-600">Donation: ${donationAmount || 0}.00</p>
      <Form.Group className="mx-auto w-full mb-3 pt-3">
        <Button size="" className="w-full mx-auto block py-2" type="submit">
          {donationIntent ? "Continue" : "Continue without Donation"}
        </Button>
      </Form.Group>
    </Form.Group>
  );
}
